import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../api/useAuth";

interface RequireRoleProps {
  roles?: string[]; // omit to just require any authenticated user
  children: ReactNode;
}

// root always passes, mirroring the backend's require_roles() behavior in
// app/core/deps.py — keep this in sync if that logic changes.
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { user, status } = useAuth();

  if (status === "loading") return <LoadingScreen />;
  if (status !== "authenticated" || !user) {
    return <Navigate to="/login" replace />;
  }
  if (roles && user.role !== "root" && !roles.includes(user.role)) {
    return <ForbiddenPage />;
  }
  return <>{children}</>;
}

// Never leave the user on a blank white page while the session is being
// rehydrated. This state is also what shows if the API can't be reached at
// all (AuthContext deliberately stays on "loading" rather than bouncing to
// /login), so a visible message makes an unreachable backend obvious.
function LoadingScreen() {
  return (
    <div className="grid min-h-screen place-items-center bg-gray-50">
      <div className="flex flex-col items-center gap-3 text-sm text-gray-500">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
        Loading…
      </div>
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div className="p-8">
      <h1 className="text-xl font-semibold">Not authorized</h1>
      <p className="text-gray-600 mt-2">
        Your role doesn't have access to this page.
      </p>
    </div>
  );
}
